import { useAuth0 } from "@auth0/auth0-react";
import { StatsigProvider, StatsigUser, useClientAsyncInit } from "@statsig/react-bindings";
import { useMemo } from "react";

import { CircularProgress } from "@/Components/v2/atoms/Progress";
import { Stack } from "@/Components/v2/atoms/Stack";
import { EnvVar } from "@/env";

import { useInfo } from "../InfoProvider/InfoProvider";

type FeatureFlagsProviderProps = {
  children?: React.ReactNode;
};

const mapEnvNameToStatsigEnvironment = (envName: string) => {
  switch (envName) {
    case "local":
      return "local";
    case "dev":
      return "development";
    case "stg":
      return "staging";
    case "prd":
      return "production";
    default:
      throw new Error(`Invalid environment name: ${envName}`);
  }
};

const FeatureFlagProvider = ({ children }: FeatureFlagsProviderProps) => {
  const { user, isAuthenticated } = useAuth0();
  const { currentOrganization } = useInfo();
  const tenantId = currentOrganization?.insightsTenantId || "";

  const userTraits = useMemo<StatsigUser>(() => {
    if (isAuthenticated) {
      return {
        userID: user!.email,
        email: user!.email || undefined,
        custom: {
          organisationName: tenantId,
        },
        statsigEnvironment: {
          tier: mapEnvNameToStatsigEnvironment(EnvVar.envName),
        },
      } as StatsigUser;
    }
    return {};
  }, [user, isAuthenticated, tenantId]);

  const { client, isLoading } = useClientAsyncInit(EnvVar.statsigFrontendKey, userTraits);

  if (isLoading) {
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ minHeight: "50rem" }}>
        <CircularProgress size={150} />
      </Stack>
    );
  }

  return <StatsigProvider client={client}>{children}</StatsigProvider>;
};

export default FeatureFlagProvider;
